import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("notifications" as any)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      const notifs = (data || []) as any[];
      setNotifications(notifs);
      setUnreadCount(notifs.filter((n: any) => !n.read_at).length);
    };
    load();

    // Realtime
    const channel = supabase
      .channel("notifications-" + user.id)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifications((prev) => [payload.new as any, ...prev]);
        setUnreadCount((c) => c + 1);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const markAllRead = async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.read_at);
    if (unread.length === 0) return;
    await supabase
      .from("notifications" as any)
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnreadCount(0);
  };

  const handleClick = async (notif: any, isGrouped: boolean) => {
    if (isGrouped && notif.data?.saved_search_id) {
      // Load saved-search filters and prefill /buscar
      const { data } = await supabase
        .from("saved_searches")
        .select("filters")
        .eq("id", notif.data.saved_search_id)
        .maybeSingle();
      if (data?.filters) {
        sessionStorage.setItem("prefilledSearchFilters", JSON.stringify(data.filters));
      }
      navigate("/buscar");
      setOpen(false);
      return;
    }
    if (notif.data?.listing_id) {
      navigate(`/patinete/${notif.data.listing_id}`);
      setOpen(false);
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markAllRead(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b border-border px-4 py-3">
          <h3 className="font-display text-sm font-semibold">Notificaciones</h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">Sin notificaciones</p>
          ) : (
            (() => {
              // Group consecutive search_alert notifications by saved_search_id (same day)
              const items: Array<{ key: string; main: any; group: any[] }> = [];
              const seenSearchKey = new Map<string, number>();
              for (const n of notifications) {
                const isSearch = n.type === "search_alert";
                const sid = n.data?.saved_search_id;
                const day = new Date(n.created_at).toISOString().slice(0, 10);
                const groupKey = isSearch && sid ? `s:${sid}:${day}` : `n:${n.id}`;
                if (isSearch && sid && seenSearchKey.has(groupKey)) {
                  items[seenSearchKey.get(groupKey)!].group.push(n);
                } else {
                  seenSearchKey.set(groupKey, items.length);
                  items.push({ key: groupKey, main: n, group: [n] });
                }
              }
              return items.map(({ key, main: n, group }) => {
                const count = group.length;
                const isGrouped = count > 1;
                return (
                  <button
                    key={key}
                    onClick={() => handleClick(n, isGrouped)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${!n.read_at ? "bg-primary/5" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium">{n.title}</p>
                      {isGrouped && (
                        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                          ×{count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isGrouped ? `${count} coincidencias nuevas para tu alerta` : n.message}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {new Date(n.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </button>
                );
              });
            })()
          )}
        </div>
        {notifications.length > 0 && (
          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={markAllRead}
              disabled={unreadCount === 0}
            >
              Marcar todas como leídas
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
